import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { CanImportCustomerResponse } from './dtos/canImportCustomerResponse';
import { Customer } from './dtos/customer';
import { CustomerActionConfiguration } from './dtos/customerActionConfiguration';
import { CustomerForm } from './dtos/customerForm';
import { CustomerPluginConfiguration } from './dtos/customerPluginConfiguration';
import { CustomerSearchForm } from './dtos/customerSearchForm';
import { CustomerWithConfigurationsForm } from './dtos/customerWithConfigurationsForm';
import { PluginFileSettingWithContent } from './dtos/pluginFileSettingWithContent';

@Injectable({ providedIn: 'root' })
export class CustomersService {

  private customersUrl = this.baseUrl + '/api/customers';

  public constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    @Inject('API_BASE_URL') private baseUrl: string) { }

  public getAllMessageTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.customersUrl + '/GetMessageTypes')
      .pipe(
        catchError(this.handleError<string[]>('getAllMessageTypes'))
      );
  }

  public getCustomerPluginConfiguration(customerId: string, pluginId: string): Observable<CustomerPluginConfiguration> {
    return this.http.get<CustomerPluginConfiguration>(this.customersUrl + '/GetPluginConfiguration/' + customerId + '/' + pluginId)
      .pipe(
        map((customerPluginConfiguration: CustomerPluginConfiguration): CustomerPluginConfiguration => {
          customerPluginConfiguration.customerId = customerId;
          customerPluginConfiguration.pluginId = pluginId;

          return customerPluginConfiguration;
        }),
        catchError(this.handleError<CustomerPluginConfiguration>('getCustomerPluginConfiguration'))
      );
  }

  public getCustomerActionConfiguration(customerId: string, actionId: string): Observable<CustomerActionConfiguration> {
    return this.http.get<CustomerActionConfiguration>(this.customersUrl + '/GetActionConfiguration/' + customerId + '/' + actionId)
      .pipe(
        map((customerActionConfiguration: CustomerActionConfiguration): CustomerActionConfiguration => {
          customerActionConfiguration.customerId = customerId;
          customerActionConfiguration.actionId = actionId;

          return customerActionConfiguration;
        }),
        catchError(this.handleError<CustomerActionConfiguration>('getCustomerActionConfiguration'))
      );
  }

  public getAllBasicInfo(filters: CustomerSearchForm): Observable<Customer[]> {
    let params = new HttpParams();
    if (filters.searchTerm){params = params.append('searchTerm', filters.searchTerm); }
    if (filters.statuses){params = params.append('statuses', filters.statuses); }
    if (filters.messageTypes){params = params.append('messageTypes', filters.messageTypes); }
    if (filters.laboratories){params = params.append('laboratories', filters.laboratories); }
    if (filters.orders){params = params.append('orders', filters.orders); }
    if (filters.results){params = params.append('results', filters.results); }
    if (filters.invoices){params = params.append('invoices', filters.invoices); }
    if (filters.actions){params = params.append('actions', filters.actions); }

    return this.http.get<Customer[]>(this.customersUrl + '/GetAllBasicInfo', {params})
      .pipe(
        catchError(this.handleError<Customer[]>('getAllBasicInfo'))
      );
  }

  public getCurrentUserAllBasicInfo(): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.customersUrl + '/GetCurrentUserAllBasicInfo')
      .pipe(
        catchError(this.handleError<Customer[]>('GetCurrentUserAllBasicInfo'))
      );
  }

  public getCustomer(id: string): Observable<Customer> {
    return this.http.get<Customer>(this.customersUrl + '/Get/' + id)
      .pipe(
        catchError(this.handleError<Customer>('getCustomer'))
      );
  }

  public addCustomer(customerToAdd: CustomerForm): Observable<string> {
    return this.http.post<string>(this.customersUrl + '/Add', customerToAdd)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('updateCustomer'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateCustomer(customerToUpdate: CustomerWithConfigurationsForm): Observable<any> {
    return this.http.put(this.customersUrl + '/Update/' + customerToUpdate.id, customerToUpdate)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<Customer>('updateCustomer'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public addOrUpdateCustomerActionConfiguration(configuration: CustomerActionConfiguration): Observable<any> {
    return this.http.post(this.customersUrl + '/AddOrUpdateCustomerActionConfiguration', configuration)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<CustomerActionConfiguration>('addOrUpdateCustomerActionConfiguration'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteCustomerActionConfiguration(customerId: string, actionId: string): Observable<any> {
    return this.http.delete(this.customersUrl + '/DeleteCustomerActionConfiguration/' + customerId + '/' + actionId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<CustomerActionConfiguration>('deleteCustomerActionConfiguration'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteCustomer(customerId: string): Observable<any> {
    return this.http.delete(this.customersUrl + '/Delete/' + customerId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<Customer>('deleteCustomer'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public canImportCustomer(customerImport: object): Observable<CanImportCustomerResponse> {
    return this.http.post<CanImportCustomerResponse>(this.customersUrl + '/CanImport', customerImport)
      .pipe(
        catchError(this.handleError<CanImportCustomerResponse>('importCustomer'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public importCustomer(customerImport: object): Observable<string> {
    return this.http.post<string>(this.customersUrl + '/Import', customerImport)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('importCustomer'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public exportCustomer(customerId: string, customerName: string): void {
    this.http.get(this.customersUrl + '/Export/' + customerId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((response: any) => {
        const blob = new Blob([JSON.stringify(response)], { type: 'application/json' });
        const link = document.createElement('a');
        link.setAttribute('href', window.URL.createObjectURL(blob));
        link.setAttribute('download', customerName);
        link.click();
      },
        catchError(this.handleError<string>('exportCustomer'))
      );
  }

  public fetchPluginCustomerConfigurationFile(pluginId: string, customerId: string, key: string): void {
    // eslint-disable-next-line max-len
    this.http.get(this.customersUrl + '/GetPluginConfigurationFile/' + customerId + '/' + pluginId + '/' + key, { responseType: 'blob', observe: 'response' })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((response: any) => {
        const headers = response.headers;
        const contentDisposition = headers.get('content-disposition').split('filename=')[1].split(';')[0];
        const blob = new Blob([response.body], { type: response.type });
        const link = document.createElement('a');
        link.setAttribute('href', window.URL.createObjectURL(blob));
        link.setAttribute('download', contentDisposition ?? 'defaultFileName.txt');
        link.click();
      },
        catchError(this.handleError<Blob>('fetchPluginCustomerConfigurationFile'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public updatePluginCustomerConfigurationFile(pluginId: string, customerId: string, pluginConfigurationFile: PluginFileSettingWithContent): Observable<any> {
    const formData = new FormData();
    if (pluginConfigurationFile.content) {
      formData.append('content', pluginConfigurationFile.content, pluginConfigurationFile.fileName);
    }
    formData.append('key', pluginConfigurationFile.key);
    formData.append('pluginId', pluginId);
    formData.append('customerId', customerId);
    return this.http.put(this.customersUrl + '/UpdatePluginCustomerConfigurationFile/', formData)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<FormData>('updatePluginConfigurationFile'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private handleError<T>(operation = 'operation', result?: T | undefined): (err: any) => Observable<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error: any): Observable<T> => {
      console.error('An error occurred', error);
      this.toastr.error(`${operation} failed: ${error.message}`);

      throw error;
    };
  }
}
